import { useEffect } from 'react'
import { useWebRTCStats } from './useWebRTCStats'
import { createLogger } from '../lib/logger'

const slog = createLogger('visio-stats')

interface StatsPanelProps {
  onClose: () => void
}

export function StatsPanel({ onClose }: StatsPanelProps) {
  const stats = useWebRTCStats(true)

  useEffect(() => {
    slog.action('stats-open')
    return () => { slog.action('stats-close') }
  }, [])

  const fmtBitrate = (bps: number) => {
    if (bps > 1_000_000) return `${(bps / 1_000_000).toFixed(1)} Mbps`
    if (bps > 1_000) return `${(bps / 1_000).toFixed(0)} kbps`
    return `${bps} bps`
  }

  const colorRtt = (ms: number) => ms < 100 ? 'text-emerald-400' : ms < 300 ? 'text-yellow-400' : 'text-red-400'
  const colorLoss = (pct: number) => pct < 1 ? 'text-emerald-400' : pct < 5 ? 'text-yellow-400' : 'text-red-400'
  const colorJitter = (ms: number) => ms < 30 ? 'text-emerald-400' : ms < 100 ? 'text-yellow-400' : 'text-red-400'

  return (
    <div className="absolute right-0 top-0 bottom-12 w-80 bg-zinc-900 border-l border-zinc-700 z-40 flex flex-col overflow-y-auto">
      <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-700">
        <span className="text-sm font-medium text-zinc-200">Statistiques</span>
        <button onClick={onClose} className="text-zinc-400 hover:text-white text-lg leading-none">&times;</button>
      </div>

      <div className="p-3 space-y-3 text-xs">
        {/* Connection */}
        <Section title="Connexion">
          <Row label="Transport" value={stats.transport.toUpperCase()} />
          <Row label="IP locale" value={stats.localIp} />
          <Row label="IP serveur" value={stats.remoteIp} />
          <Row label="Latence (RTT)" value={`${stats.rtt} ms`} className={colorRtt(stats.rtt)} />
        </Section>

        {/* Audio */}
        <Section title="Audio">
          <Row label="Codec envoi" value={stats.audioSendCodec} />
          <Row label="Debit envoi" value={fmtBitrate(stats.audioSendBitrate)} />
          <Row label="Codec reception" value={stats.audioRecvCodec} />
          <Row label="Debit reception" value={fmtBitrate(stats.audioRecvBitrate)} />
          <Row label="Jitter" value={`${stats.audioRecvJitter} ms`} className={colorJitter(stats.audioRecvJitter)} />
          <Row label="Perte paquets" value={`${stats.audioRecvPacketLoss}%`} className={colorLoss(stats.audioRecvPacketLoss)} />
        </Section>

        {/* Video */}
        <Section title="Video">
          <Row label="Codec envoi" value={stats.videoSendCodec} />
          <Row label="Resolution envoi" value={stats.videoSendWidth ? `${stats.videoSendWidth}x${stats.videoSendHeight}` : '-'} />
          <Row label="FPS envoi" value={`${stats.videoSendFps}`} />
          <Row label="Debit envoi" value={fmtBitrate(stats.videoSendBitrate)} />
          <Row label="Perte paquets envoi" value={`${stats.videoSendPacketLoss}%`} className={colorLoss(stats.videoSendPacketLoss)} />
          <Row label="Codec reception" value={stats.videoRecvCodec} />
          <Row label="Resolution reception" value={stats.videoRecvWidth ? `${stats.videoRecvWidth}x${stats.videoRecvHeight}` : '-'} />
          <Row label="FPS reception" value={`${stats.videoRecvFps}`} />
          <Row label="Debit reception" value={fmtBitrate(stats.videoRecvBitrate)} />
          <Row label="Perte paquets reception" value={`${stats.videoRecvPacketLoss}%`} className={colorLoss(stats.videoRecvPacketLoss)} />
        </Section>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-1">{title}</div>
      <div className="bg-zinc-800 rounded p-2 space-y-1">{children}</div>
    </div>
  )
}

function Row({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-zinc-500">{label}</span>
      <span className={`font-mono ${className || 'text-zinc-300'}`}>{value}</span>
    </div>
  )
}
